import { useEffect, useState } from "react";
import { Flame, CheckCircle2, XCircle, Trophy, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { MathText } from "@/components/MathText";
import { SUBJECT_LABELS, EXAM_LABELS, subjectAccent } from "@/lib/pyqMeta";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const LETTERS = { a: "A", b: "B", c: "C", d: "D" };

export const DailyChallengeDialog = ({ open, onOpenChange, questionId, onCompleted }) => {
  const [q, setQ] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [startAt] = useState(Date.now());

  useEffect(() => {
    if (open && questionId) {
      setQ(null); setSelected(null); setSubmitted(false); setResult(null);
      api.get(`/pyq/${questionId}`).then((r) => setQ(r.data)).catch(() => {});
    }
  }, [open, questionId]);

  const correctList = () => (Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer]);

  const submit = async () => {
    if (!selected || submitted) return;
    const isCorrect = correctList().includes(selected);
    setSubmitted(true);
    const timeSpentSec = Math.round((Date.now() - startAt) / 1000);
    try {
      await api.post("/attempts", { questionId: q.id, selectedAnswer: selected, isCorrect, timeSpentSec });
      const res = await api.post("/student/daily-challenge/complete", { isCorrect });
      setResult({ isCorrect, ...res.data });
      onCompleted?.();
    } catch {
      setResult({ isCorrect, bonus: 0, streak: 0, badges: [], error: true });
    }
  };

  const optState = (id) => {
    if (!submitted) return selected === id ? "selected" : "default";
    if (correctList().includes(id)) return "correct";
    if (selected === id) return "incorrect";
    return "default";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="daily-challenge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-amber-500" /> Challenge of the Day
          </DialogTitle>
        </DialogHeader>

        {!q ? (
          <div className="space-y-3"><Skeleton className="h-6 w-1/3" /><Skeleton className="h-20 w-full" /><Skeleton className="h-12 w-full" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className={cn("font-semibold", subjectAccent(q.subject))}>{SUBJECT_LABELS[q.subject] || q.subject}</span>
              <span>·</span><span>{EXAM_LABELS[q.examType]}</span>
              {q.chapter && (<><span>·</span><span>{q.chapter}</span></>)}
            </div>

            <MathText className="text-[15px] leading-relaxed" text={q.questionText} />
            {q.diagram && <div className="overflow-x-auto rounded-lg border border-border p-2" dangerouslySetInnerHTML={{ __html: q.diagram }} />}

            <div className="space-y-2.5">
              {q.options.map((opt) => {
                const s = optState(opt.id);
                return (
                  <button key={opt.id} type="button" disabled={submitted}
                    data-testid={`daily-option-${opt.id}`}
                    onClick={() => setSelected(opt.id)}
                    className={cn("flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                      s === "default" && "border-border bg-card hover:border-primary/50",
                      s === "selected" && "border-primary bg-primary/5",
                      s === "correct" && "border-green-500 bg-green-50 dark:bg-green-950/40",
                      s === "incorrect" && "border-red-500 bg-red-50 dark:bg-red-950/40")}>
                    <span className={cn("flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border text-sm font-bold",
                      s === "default" && "border-border bg-secondary text-muted-foreground",
                      s === "selected" && "border-primary bg-primary text-primary-foreground",
                      s === "correct" && "border-green-500 bg-green-500 text-white",
                      s === "incorrect" && "border-red-500 bg-red-500 text-white")}>
                      {LETTERS[opt.id] || opt.id.toUpperCase()}
                    </span>
                    <MathText className="flex-1 text-sm" text={opt.text} />
                    {s === "correct" && <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-500" />}
                    {s === "incorrect" && <XCircle className="h-5 w-5 flex-shrink-0 text-red-500" />}
                  </button>
                );
              })}
            </div>

            {!submitted ? (
              <Button data-testid="daily-submit" onClick={submit} disabled={!selected} className="w-full rounded-xl">Submit answer</Button>
            ) : result && (
              <div className="space-y-3" data-testid="daily-result">
                <div className={cn("rounded-xl border p-4 text-center", result.isCorrect ? "border-green-500 bg-green-50 dark:bg-green-950/30" : "border-red-400 bg-red-50 dark:bg-red-950/30")}>
                  <div className="font-heading text-lg font-semibold">{result.isCorrect ? "Correct! 🎉" : "Not quite"}</div>
                  {result.alreadyDone ? (
                    <p className="mt-1 text-sm text-muted-foreground">You already completed today's challenge.</p>
                  ) : (
                    <div className="mt-2 flex items-center justify-center gap-4 text-sm">
                      <span className="inline-flex items-center gap-1 font-semibold text-amber-600"><Sparkles className="h-4 w-4" />+{result.bonus} pts</span>
                      <span className="inline-flex items-center gap-1 font-semibold text-orange-600"><Flame className="h-4 w-4" />{result.streak}-day streak</span>
                    </div>
                  )}
                  {result.badges?.length > 0 && (
                    <div className="mt-3 flex flex-wrap justify-center gap-2">
                      {result.badges.map((b) => (
                        <span key={b} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`badge-${b}`}>
                          <Trophy className="h-3.5 w-3.5" />{b}-day
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {q.solution?.steps?.length > 0 && (
                  <div className="rounded-xl border border-border p-4">
                    <div className="mb-2 text-sm font-semibold">Solution</div>
                    <div className="space-y-2">
                      {q.solution.steps.map((st, i) => (<MathText key={i} className="text-sm leading-relaxed" text={st} />))}
                    </div>
                    {q.solution.finalAnswer && <MathText block className="mt-2 text-sm font-medium" text={q.solution.finalAnswer} />}
                  </div>
                )}
                <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full rounded-xl">Done</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
